import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiError, inr, inrFull, num, resolveImg, imgFallback } from "@/lib/api";
import { brandTypeClass } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChartBar, Package, Handshake, Bank, Gear, UploadSimple, CheckCircle, XCircle, Warning, Star, FileArrowDown, FileXls, UsersThree, UserPlus, Trash, MagnifyingGlass, Eye, EyeSlash, Plus } from "@phosphor-icons/react";

const LOT_STATUS = {
  draft: "bg-navy/10 text-navy", under_review: "bg-warn/15 text-warn", submitted: "bg-warn/15 text-warn",
  published: "bg-ok/15 text-ok", rejected: "bg-destructive/10 text-destructive",
  suspended: "bg-destructive/10 text-destructive", sold: "bg-navy text-white", closed: "bg-navy/10 text-navy",
};
const EOI_STAGES = ["new", "contacted", "negotiating", "won", "lost", "closed"];
const EOI_STYLE = { new: "border-navy/30 bg-navy/5", contacted: "border-warn/40 bg-warn/5", negotiating: "border-brand/40 bg-brand/5", won: "border-ok/40 bg-ok/5", lost: "border-destructive/30 bg-destructive/5", closed: "border-border bg-navy-50" };

function Kpi({ label, value, tone }) {
  return (
    <div className="bg-navy-50 p-5">
      <div className={`stat text-2xl font-bold ${tone || "text-navy"}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return <div className="py-16 text-center text-muted-foreground">Loading analytics...</div>;
  const s = a.lot_status_counts || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border md:grid-cols-4">
        <Kpi label="Total lots" value={a.total_lots} />
        <Kpi label="Published value" value={inr(a.published_value)} tone="text-brand" />
        <Kpi label="Total lot views" value={num(a.total_views)} />
        <Kpi label="File downloads" value={num(a.total_downloads)} />
        <Kpi label="Total EOIs" value={a.eoi_total} />
        <Kpi label="New EOIs" value={a.eoi_new} tone="text-warn" />
        <Kpi label="EOIs won" value={a.eoi_won} tone="text-ok" />
        <Kpi label="Deals tracked" value={a.deals_total} />
      </div>
      <div className="rounded-sm border border-border bg-white p-6">
        <h3 className="font-display font-bold text-navy">Lots by status</h3>
        <div className="mt-4 flex flex-wrap gap-3">
          {Object.entries(s).map(([k, v]) => (
            <span key={k} className={`rounded-sm px-3 py-1.5 text-sm font-semibold uppercase tracking-wider ${LOT_STATUS[k] || "bg-navy/10 text-navy"}`}>{k.replace(/_/g, " ")}: <span className="stat">{v}</span></span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Lots moderation ---------- */
function LotsModeration() {
  const [lots, setLots] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null); // lot being reviewed
  const [reason, setReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [sanitizedId, setSanitizedId] = useState(null);
  const [sanitizedName, setSanitizedName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delLot, setDelLot] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/lots", { params: { status: filter } }).then(({ data }) => setLots(data.lots));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openReview = (lot) => {
    setActive(lot); setReason(lot.rejection_reason || ""); setAdminNotes(lot.admin_notes || "");
    setSanitizedId(lot.sanitized_excel_file_id); setSanitizedName(lot.sanitized_excel_file_id ? "Sanitized file attached" : "");
  };

  const uploadSanitized = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("purpose", "sanitized_excel");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSanitizedId(data.id); setSanitizedName(data.filename);
      toast.success("Sanitized file uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const downloadOriginal = async (fileId) => {
    try {
      const res = await fetch(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("sh_token")}` } });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "original_inventory";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download original file"); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/lots/${active.id}/action`, { action, reason, admin_notes: adminNotes, sanitized_excel_file_id: sanitizedId });
      toast.success(`Lot ${action === "publish" ? "published" : action}`);
      setActive(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const quickAction = async (lot, action) => {
    try { await api.post(`/admin/lots/${lot.id}/action`, { action }); toast.success(`Done: ${action}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doDeleteLot = async () => {
    try { await api.delete(`/admin/lots/${delLot.id}`); toast.success("Lot deleted"); setDelLot(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 rounded-sm" data-testid="admin-lot-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "under_review", "published", "rejected", "suspended", "sold", "closed", "draft"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All lots" : s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{lots.length} lots</span>
        <Link to="/seller/new" className="ml-auto inline-flex items-center gap-1 rounded-sm bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover" data-testid="admin-create-lot-button"><Plus size={16} /> Create &amp; publish lot</Link>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-navy text-white text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Lot</th><th className="px-4 py-3">Seller</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">MRP</th><th className="px-4 py-3 text-right">Asking</th><th className="px-4 py-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lots.map((l) => (
              <tr key={l.id} className="bg-white hover:bg-navy-50" data-testid={`admin-lot-${l.id}`}>
                <td className="px-4 py-3 max-w-[240px]">
                  <div className="flex items-center gap-2">
                    <img src={resolveImg(l.image_ids?.[0])} onError={imgFallback} alt="" className="h-9 w-12 rounded-sm object-cover" />
                    <div className="min-w-0">
                      <span className="block font-medium text-navy line-clamp-2">{l.title}</span>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {l.vehicle_type && <span data-testid={`admin-lot-vehicle-type-${l.id}`} className="rounded bg-navy/5 px-1.5 py-0.5 text-[10px] font-semibold text-navy">{l.vehicle_type}</span>}
                        {l.brand_type && <span data-testid={`admin-lot-brand-type-${l.id}`} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${brandTypeClass(l.brand_type)}`}>{l.brand_type}</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{l.seller_company || l.seller_name}</td>
                <td className="px-4 py-3"><span className={`rounded-sm px-2 py-1 text-xs font-semibold uppercase ${LOT_STATUS[l.status]}`}>{l.status.replace("_", " ")}</span></td>
                <td className="stat px-4 py-3 text-right">{inr(l.mrp_value)}</td>
                <td className="stat px-4 py-3 text-right text-brand font-bold">{inr(l.asking_price)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Link to={`/seller/edit/${l.id}`} className="inline-flex h-7 items-center rounded-sm border border-border px-2 text-xs font-medium text-navy hover:bg-navy-50" data-testid={`edit-lot-${l.id}`}>Edit</Link>
                    <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs" onClick={() => openReview(l)} data-testid={`review-lot-${l.id}`}>Review</Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs text-destructive" onClick={() => setDelLot(l)} data-testid={`delete-lot-${l.id}`}><Trash size={12} /></Button>
                    {l.status === "published" && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs" onClick={() => quickAction(l, l.featured ? "unfeature" : "feature")} data-testid={`feature-lot-${l.id}`}><Star size={12} className={l.featured ? "text-brand" : ""} weight={l.featured ? "fill" : "regular"} /></Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs text-destructive" onClick={() => quickAction(l, "suspend")}>Suspend</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs" onClick={() => quickAction(l, "sold")}>Mark sold</Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Review dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="lot-review-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{active.title}</DialogTitle>
                <DialogDescription>Review seller submission, sanitize the file, then publish or reject.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border text-sm sm:grid-cols-4">
                  <div className="bg-navy-50 p-3"><div className="text-xs text-muted-foreground">MRP</div><div className="stat font-bold">{inr(active.mrp_value)}</div></div>
                  <div className="bg-navy-50 p-3"><div className="text-xs text-muted-foreground">Asking</div><div className="stat font-bold text-brand">{inr(active.asking_price)}</div></div>
                  <div className="bg-navy-50 p-3"><div className="text-xs text-muted-foreground">Units</div><div className="stat font-bold">{num(active.total_units)}</div></div>
                  <div className="bg-navy-50 p-3"><div className="text-xs text-muted-foreground">SKUs</div><div className="stat font-bold">{num(active.unique_skus)}</div></div>
                </div>
                <div className="text-sm"><span className="text-muted-foreground">Seller:</span> <span className="font-medium text-navy">{active.seller_name} · {active.seller_email}</span></div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span data-testid="admin-review-vehicle-type" className="rounded-sm bg-navy/5 px-2.5 py-1 font-semibold text-navy">Vehicle: {active.vehicle_type || "—"}</span>
                  <span data-testid="admin-review-brand-type" className={`rounded-sm px-2.5 py-1 font-semibold ${brandTypeClass(active.brand_type)}`}>Brand: {active.brand_type || "—"}</span>
                  {active.category && <span className="rounded-sm border border-border px-2.5 py-1 text-subtext">{active.category}</span>}
                </div>
                {active.mrp_value < 500000 && <div className="flex items-center gap-2 rounded-sm border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"><Warning weight="fill" /> Below ₹5 lakh minimum threshold.</div>}

                {active.image_ids?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto">{active.image_ids.map((im, i) => <img key={i} src={resolveImg(im)} onError={imgFallback} alt="" className="h-16 w-24 shrink-0 rounded-sm object-cover" />)}</div>
                )}

                <div>
                  <Label>Original seller file (private)</Label>
                  {active.original_excel_file_id ? (
                    <button onClick={() => downloadOriginal(active.original_excel_file_id)} className="mt-1.5 flex w-full items-center gap-2 rounded-sm border border-border bg-navy-50 p-3 text-left text-sm text-navy hover:border-navy/40" data-testid="admin-original-file"><FileXls size={18} /> Download original inventory <FileArrowDown className="ml-auto text-brand" /></button>
                  ) : <p className="mt-1.5 text-sm text-muted-foreground">No original file uploaded by seller.</p>}
                </div>

                <div>
                  <Label>Sanitized buyer-facing file (required to publish)</Label>
                  <label className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-border bg-navy-50 p-3 hover:border-navy/40" data-testid="sanitized-upload-label">
                    <FileXls size={18} className="text-navy" />
                    <span className="flex-1 text-sm">{sanitizedName || <span className="text-muted-foreground">Upload sanitized .xlsx / .csv</span>}</span>
                    <UploadSimple className="text-brand" />
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="sanitized-input" onChange={(e) => e.target.files[0] && uploadSanitized(e.target.files[0])} />
                  </label>
                  {uploading && <p className="mt-1 text-xs text-muted-foreground">Uploading...</p>}
                </div>

                <div><Label>Internal admin notes</Label><Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} className="mt-1.5 rounded-sm" data-testid="admin-notes-input" /></div>
                <div><Label>Rejection / suspension reason (if applicable)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 rounded-sm" data-testid="rejection-reason-input" /></div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button onClick={() => act("publish")} disabled={busy} className="rounded-sm bg-ok text-white hover:bg-ok/90" data-testid="publish-lot-button"><CheckCircle className="mr-1" /> Publish</Button>
                <Button onClick={() => act("reject")} disabled={busy} variant="outline" className="rounded-sm text-destructive" data-testid="reject-lot-button"><XCircle className="mr-1" /> Reject</Button>
                <Button onClick={() => act("suspend")} disabled={busy} variant="outline" className="rounded-sm" data-testid="suspend-lot-button">Suspend</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete lot confirm */}
      <Dialog open={!!delLot} onOpenChange={(o) => !o && setDelLot(null)}>
        <DialogContent className="rounded-sm" data-testid="delete-lot-dialog">
          <DialogHeader><DialogTitle className="font-display">Delete lot?</DialogTitle><DialogDescription>This permanently removes "{delLot?.title}" along with its expressions of interest. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDelLot(null)}>Cancel</Button>
            <Button className="rounded-sm bg-destructive text-white hover:bg-destructive/90" onClick={doDeleteLot} data-testid="confirm-delete-lot">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- EOI pipeline ---------- */
function EoiPipeline() {
  const [eois, setEois] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [sales, setSales] = useState("");
  const load = useCallback(() => api.get("/admin/eois").then(({ data }) => setEois(data.eois)), []);
  useEffect(() => { load(); }, [load]);

  const move = async (eoi, status) => {
    try { await api.put(`/admin/eois/${eoi.id}`, { status }); toast.success(`Moved to ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveDetail = async () => {
    try { await api.put(`/admin/eois/${active.id}`, { salesperson: sales, note: note || undefined }); toast.success("Updated"); setNote(""); setActive(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const byStage = (s) => eois.filter((e) => e.status === s);

  return (
    <div>
      <div className="grid gap-4 overflow-x-auto md:grid-cols-3 xl:grid-cols-6">
        {EOI_STAGES.map((stage) => (
          <div key={stage} className="min-w-[220px] rounded-sm border border-border bg-navy-50">
            <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">{stage}</span>
              <span className="stat text-xs text-muted-foreground">{byStage(stage).length}</span>
            </div>
            <div className="space-y-2 p-2" data-testid={`eoi-stage-${stage}`}>
              {byStage(stage).map((e) => (
                <div key={e.id} className={`rounded-sm border p-3 ${EOI_STYLE[stage]}`} data-testid={`eoi-card-${e.id}`}>
                  <div className="text-sm font-semibold text-navy line-clamp-2">{e.lot_title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{e.buyer_company || e.buyer_name}</div>
                  <div className="stat mt-1 text-sm font-bold text-brand">{inrFull(e.indicative_price)}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button onClick={() => { setActive(e); setSales(e.salesperson || ""); }} className="rounded-sm border border-navy/20 px-2 py-0.5 text-[11px] text-navy hover:bg-navy hover:text-white" data-testid={`eoi-open-${e.id}`}>Details</button>
                    <Select value={e.status} onValueChange={(v) => move(e, v)}>
                      <SelectTrigger className="h-6 w-24 rounded-sm px-2 text-[11px]" data-testid={`eoi-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{EOI_STAGES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              {byStage(stage).length === 0 && <div className="px-2 py-4 text-center text-xs text-muted-foreground">Empty</div>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-sm" data-testid="eoi-detail-dialog">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-display">{active.lot_title}</DialogTitle><DialogDescription>EOI from {active.buyer_name}</DialogDescription></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-muted-foreground">Buyer</span><div className="font-medium text-navy">{active.buyer_name}</div></div>
                  <div><span className="text-muted-foreground">Company</span><div className="font-medium text-navy">{active.buyer_company || "—"}</div></div>
                  <div><span className="text-muted-foreground">Email</span><div className="font-medium text-navy">{active.buyer_email}</div></div>
                  <div><span className="text-muted-foreground">Phone</span><div className="font-medium text-navy">{active.buyer_phone || "—"}</div></div>
                </div>
                <div className="rounded-sm border border-brand/30 bg-brand/5 p-3"><span className="text-xs text-muted-foreground">Indicative price (admin-only)</span><div className="stat text-lg font-bold text-brand">{inrFull(active.indicative_price)}</div></div>
                <div><span className="text-muted-foreground">Buyer notes</span><div className="text-navy">{active.notes || "—"}</div></div>
                <div><Label>Assigned salesperson</Label><Input value={sales} onChange={(e) => setSales(e.target.value)} className="mt-1.5 rounded-sm" data-testid="eoi-salesperson" placeholder="e.g. Vikram" /></div>
                <div><Label>Add internal note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5 rounded-sm" data-testid="eoi-note-input" /></div>
                {active.internal_notes?.length > 0 && (
                  <div className="space-y-1 rounded-sm border border-border bg-navy-50 p-3 text-xs">
                    {active.internal_notes.map((n, i) => <div key={i}><span className="font-semibold text-navy">{n.by}:</span> <span className="text-muted-foreground">{n.text}</span></div>)}
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={saveDetail} className="rounded-sm bg-navy text-white" data-testid="eoi-save">Save</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Deals / escrow ---------- */
const ESCROW = ["not_started", "initiated", "funds_in_escrow", "released", "refunded"];
const DEAL = ["negotiating", "agreed", "sold", "closed", "cancelled"];

function Deals() {
  const [deals, setDeals] = useState([]);
  const [lots, setLots] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lot_id: "", buyer_name: "", final_price: "", negotiation_notes: "", partial_lot_notes: "", escrow_status: "not_started", deal_status: "negotiating" });
  const load = useCallback(() => api.get("/admin/deals").then(({ data }) => setDeals(data.deals)), []);
  useEffect(() => { load(); api.get("/admin/lots").then(({ data }) => setLots(data.lots)); }, [load]);

  const save = async () => {
    if (!form.lot_id) { toast.error("Select a lot"); return; }
    try { await api.post("/admin/deals", { ...form, final_price: Number(form.final_price || 0) }); toast.success("Deal created"); setOpen(false); setForm({ lot_id: "", buyer_name: "", final_price: "", negotiation_notes: "", partial_lot_notes: "", escrow_status: "not_started", deal_status: "negotiating" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const update = async (deal, patch) => {
    try { await api.put(`/admin/deals/${deal.id}`, { ...deal, ...patch }); load(); toast.success("Updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end"><Button onClick={() => setOpen(true)} className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="new-deal-button">+ New deal</Button></div>
      {deals.length === 0 ? (
        <div className="rounded-sm border border-dashed border-border p-16 text-center"><Bank size={40} className="mx-auto text-navy/20" /><p className="mt-3 font-display font-semibold text-navy">No deals tracked yet</p><p className="text-sm text-muted-foreground">Create a deal to track offline negotiation and escrow.</p></div>
      ) : (
        <div className="space-y-3">
          {deals.map((d) => (
            <div key={d.id} className="rounded-sm border border-border bg-white p-5" data-testid={`deal-${d.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display font-semibold text-navy">{d.lot_title}</div>
                  <div className="text-sm text-muted-foreground">Buyer: {d.buyer_name || "—"} · Final: <span className="stat font-bold text-brand">{inrFull(d.final_price)}</span></div>
                  {d.partial_lot_notes && <div className="mt-1 text-xs text-warn">Partial lot: {d.partial_lot_notes}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={d.deal_status} onValueChange={(v) => update(d, { deal_status: v })}><SelectTrigger className="h-8 w-32 rounded-sm text-xs" data-testid={`deal-status-${d.id}`}><SelectValue /></SelectTrigger><SelectContent>{DEAL.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent></Select>
                  <Select value={d.escrow_status} onValueChange={(v) => update(d, { escrow_status: v })}><SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid={`escrow-status-${d.id}`}><SelectValue /></SelectTrigger><SelectContent>{ESCROW.map((s) => <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="deal-dialog">
          <DialogHeader><DialogTitle className="font-display">Create deal record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Lot</Label>
              <Select value={form.lot_id} onValueChange={(v) => setForm({ ...form, lot_id: v })}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="deal-lot-select"><SelectValue placeholder="Select lot" /></SelectTrigger>
                <SelectContent>{lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Buyer name</Label><Input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="deal-buyer" /></div>
              <div><Label>Final price (₹)</Label><Input type="number" value={form.final_price} onChange={(e) => setForm({ ...form, final_price: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="deal-price" /></div>
            </div>
            <div><Label>Negotiation notes</Label><Textarea value={form.negotiation_notes} onChange={(e) => setForm({ ...form, negotiation_notes: e.target.value })} rows={2} className="mt-1.5 rounded-sm" /></div>
            <div><Label>Partial-lot notes</Label><Input value={form.partial_lot_notes} onChange={(e) => setForm({ ...form, partial_lot_notes: e.target.value })} className="mt-1.5 rounded-sm" placeholder="e.g. electricals only" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-navy text-white" data-testid="deal-save">Create deal</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Settings ---------- */
function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try { await api.put("/admin/settings", { default_success_fee_percent: Number(s.default_success_fee_percent), min_lot_value_threshold: Number(s.min_lot_value_threshold), escrow_partner: s.escrow_partner || "" }); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!s) return <div className="py-16 text-center text-muted-foreground">Loading...</div>;
  return (
    <div className="max-w-lg space-y-5 rounded-sm border border-border bg-white p-6">
      <h3 className="font-display font-bold text-navy">Commercial settings</h3>
      <div><Label>Default success fee (%)</Label><Input type="number" value={s.default_success_fee_percent} onChange={(e) => setS({ ...s, default_success_fee_percent: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="settings-fee" /></div>
      <div><Label>Minimum lot MRP value (₹)</Label><Input type="number" value={s.min_lot_value_threshold} onChange={(e) => setS({ ...s, min_lot_value_threshold: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="settings-threshold" /></div>
      <div><Label>Escrow partner (shown to users)</Label><Input value={s.escrow_partner || ""} onChange={(e) => setS({ ...s, escrow_partner: e.target.value })} className="mt-1.5 rounded-sm" data-testid="settings-escrow" /></div>
      <Button onClick={save} className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="settings-save">Save settings</Button>
    </div>
  );
}

/* ---------- Users management ---------- */
function Users() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer", company: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [delUser, setDelUser] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users", { params: { role: role !== "all" ? role : undefined, q: q || undefined } })
      .then(({ data }) => setUsers(data.users))
      .catch(() => toast.error("Could not load users. Please retry."))
      .finally(() => setLoading(false));
  }, [role, q]);
  useEffect(() => { setLoading(true); const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const addUser = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", role: "buyer", company: "", phone: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/admin/users/${u.id}`, { active: !(u.active !== false) }); toast.success(u.active !== false ? "User deactivated" : "User activated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/users/${delUser.id}`); toast.success("User deleted"); setDelUser(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            <SelectItem value="buyer">Buyers</SelectItem>
            <SelectItem value="seller">Sellers</SelectItem>
          </SelectContent>
        </Select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex min-w-[220px] flex-1 gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company..." className="rounded-sm" data-testid="users-search" />
          <Button type="submit" variant="outline" className="shrink-0 rounded-sm" data-testid="users-search-button"><MagnifyingGlass size={16} /></Button>
        </form>
        <Button onClick={() => setAddOpen(true)} className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="add-user-button"><UserPlus className="mr-1" size={16} /> Add user</Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="rounded-sm border border-dashed border-border p-16 text-center"><UsersThree size={40} className="mx-auto text-navy/20" /><p className="mt-3 font-display font-semibold text-navy">No users found</p></div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-border">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="bg-navy text-white text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Activity</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="bg-white hover:bg-navy-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-navy">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                  <td className="px-4 py-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold uppercase ${u.role === "seller" ? "bg-darkteal/10 text-darkteal" : "bg-navy/10 text-navy"}`}>{u.role}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.company || "—"}</td>
                  <td className="stat px-4 py-3 text-muted-foreground">{u.role === "seller" ? `${u.lot_count ?? 0} lots` : `${u.eoi_count ?? 0} EOIs`}</td>
                  <td className="px-4 py-3">{u.active === false
                    ? <span className="rounded-sm bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive" data-testid={`user-status-${u.id}`}>Inactive</span>
                    : <span className="rounded-sm bg-ok/15 px-2 py-0.5 text-xs font-semibold text-ok" data-testid={`user-status-${u.id}`}>Active</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>{u.active === false ? "Activate" : "Deactivate"}</Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm px-2 text-xs text-destructive" onClick={() => setDelUser(u)} data-testid={`delete-user-${u.id}`}><Trash size={12} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-sm" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Add a buyer or seller</DialogTitle><DialogDescription>Create a portal account. They can log in immediately with these credentials.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="new-user-name" /></div>
              <div><Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="buyer">Buyer</SelectItem><SelectItem value="seller">Seller</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="new-user-email" /></div>
            <div><Label>Temporary password *</Label>
              <div className="relative mt-1.5">
                <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm pr-10" data-testid="new-user-password" placeholder="Min 6 characters" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy" data-testid="toggle-password-visibility">{showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-sm" data-testid="new-user-company" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm" data-testid="new-user-phone" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addUser} disabled={saving} className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="save-user-button">{saving ? "Creating..." : "Create user"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="rounded-sm" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Delete user?</DialogTitle><DialogDescription>This permanently removes {delUser?.name} ({delUser?.email}). This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDelUser(null)}>Cancel</Button>
            <Button className="rounded-sm bg-destructive text-white hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-user">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Operations</span>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-navy">Admin dashboard</h1>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 rounded-sm bg-navy-50 p-1">
          <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-overview"><ChartBar className="mr-1" size={16} /> Overview</TabsTrigger>
          <TabsTrigger value="lots" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-lots"><Package className="mr-1" size={16} /> Lots</TabsTrigger>
          <TabsTrigger value="eoi" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-eoi"><Handshake className="mr-1" size={16} /> EOI Pipeline</TabsTrigger>
          <TabsTrigger value="deals" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-deals"><Bank className="mr-1" size={16} /> Deals & Escrow</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-users"><UsersThree className="mr-1" size={16} /> Users</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-sm data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-settings"><Gear className="mr-1" size={16} /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
        <TabsContent value="lots" className="mt-6"><LotsModeration /></TabsContent>
        <TabsContent value="eoi" className="mt-6"><EoiPipeline /></TabsContent>
        <TabsContent value="deals" className="mt-6"><Deals /></TabsContent>
        <TabsContent value="users" className="mt-6"><Users /></TabsContent>
        <TabsContent value="settings" className="mt-6"><Settings /></TabsContent>
      </Tabs>
    </div>
  );
}
